import { Component, ElementRef, Inject, LOCALE_ID, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {MatRadioModule} from '@angular/material/radio';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, timer } from 'rxjs';
import { ApiService, changeMfaMethodAPI, verifyOTPAPI } from 'src/app/api/api.service';
import { dashboardRoute } from 'src/app/route-names';
import { DialogType } from 'src/app/Services/dialog.service';
import { ElectorDataService, MfaMethod } from 'src/app/Services/elector-data.service';
import { GenericDialogComponent } from 'src/app/shared/generic-dialog/generic-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
    selector: 'app-change-mfa',
    imports: [FormsModule, MatRadioModule, MatProgressSpinnerModule, MatStepperModule],
    templateUrl: './change-mfa.component.html',
    styleUrl: './change-mfa.component.scss'
})
export class ChangeMfaComponent implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper | undefined;
  @ViewChildren('mfaRef') mfaInputFields!: QueryList<ElementRef>;
  isNotMobileScreen = this.breakpointObserver.isMatched('(min-width: 600px)');
  isNotBigScreen = this.breakpointObserver.isMatched('(max-width: 1024px)');
  isLinear = true;
  originalMfaMethod: MfaMethod = 'EMAIL';
  chosenMfaMethod: MfaMethod = 'APP';
  dialogMessage?: string;
  dialogTitle?: string;

  public displaySpinner: boolean = false;

  maxMFACodeAttemptsReached: boolean = false;
  maxMFAResendsReached: boolean = false;

  qrScanned = false;
  token: string | undefined;
  showCode = false;

  numbers = [0, 1, 2, 3, 4, 5];
  resendAllowedTimer: Observable<number> | undefined;
  allowResends: boolean = false;

  mfaFormGroup: FormGroup;
 
  constructor(
    private breakpointObserver: BreakpointObserver,
    readonly electorDataService: ElectorDataService,
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog,
    private formBuilder: FormBuilder,
    private readonly router: Router,
    @Inject(LOCALE_ID) readonly locale: string) {
      this.mfaFormGroup = this.formBuilder.group({
        code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
      });
      
    }

  ngOnInit(): void {
    this.originalMfaMethod = this.electorDataService.userAuthData()?.mfaMethod ?? 'EMAIL';
    this.chosenMfaMethod = 'APP';
    if(this.electorDataService.userAuthData()) {
      this.electorDataService.userAuthData()!.attemptsRemaining = this.electorDataService.MAX_OTP_ATTEMPTS;
    }
  }

  back() {
    this.stepper?.previous();
  }

  backToQR() {
    this.qrScanned = false;
  }

  toCodeEntry() {
    this.qrScanned = true;
  }

  async cancel() {
    this.chosenMfaMethod = this.originalMfaMethod;
    this.router.navigate(dashboardRoute);
  }

  async postMFAMethod() {
    if (this.electorDataService.userAuthData()?.voter_id === undefined) {
      this.dialogTitle = $localize`Information not found`;
      this.dialogMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
      this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
    }
    this.electorDataService.userAuthData()!.mfaMethod = this.chosenMfaMethod;
    try {
      this.displaySpinner = true;
      let response = await firstValueFrom(this.apiService.postWithHandshakeCheck<{ token: string }>(changeMfaMethodAPI, {
        voter_id: this.electorDataService.userAuthData()?.voter_id,
        mfa_method: this.chosenMfaMethod,
        new_method: true,
        locale: this.locale
      }));
      this.displaySpinner = false;
      if (response.body) {
        if (this.electorDataService.userAuthData()?.mfaMethod === 'SMS') {
          this.dialogTitle = $localize`Code sent`;
          this.dialogMessage = $localize`A new code has been sent. Please note that it can take a few seconds for the SMS to be delivered. If you have not received the SMS, please also check your email for the new code.`;
          this.resetTimer();
          this.openDialog({ type: DialogType.SUCCESS, title: this.dialogTitle, content: this.dialogMessage });
        } else if (this.electorDataService.userAuthData()?.mfaMethod === 'EMAIL') {
          this.dialogTitle = $localize`Code sent`;
          this.dialogMessage = $localize`A new code has been sent. Please note that it can take a few seconds for the email to be delivered. If you have not received the email, please use the "Contact Support" link for assistance.`;
          this.resetTimer();
          this.openDialog({ type: DialogType.SUCCESS, title: this.dialogTitle, content: this.dialogMessage });
        } else {
          this.token = response.body.token;
          this.electorDataService.setAuthAppSecret(this.electorDataService.userAuthData()!.voter_id, response.body.token);
        }
        if(this.stepper) {
          if(this.stepper.selected) {
            this.stepper.selected.completed = true;
          }
          this.stepper.next();
        }
      } else {
        this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
        this.openDialog({ type: DialogType.ERROR, title: $localize`Unknown error.`, content: this.dialogMessage });
      }
    } catch (err: any) {
      this.displaySpinner = false;
      this.resetTimer();
      switch (err.error.error_type) {
        case 'UNKNOWN_USER':
          this.dialogTitle = $localize`Information not found`;
          this.dialogMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage })
          break;
        case 'BAD_REQUEST':
          this.dialogMessage = $localize` Please try again or use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Bad Request.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'INVALID_OTP':
          this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 1) - 1;
          this.dialogMessage = $localize`The verification code is incorrect. To continue, please try entering it again.`;
          this.dialogTitle = $localize`Invalid Code`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break
        case 'OTP_EXPIRED':
          this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 1) - 1;
          if ((this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) <= 0) {
            this.dialogMessage = $localize`You have exhausted attempts to enter the verification code, to continue, please press the OK button to return to the login screen and try to enter again.`;
            this.dialogTitle = $localize`Maximum Attempts Reached.`
          } else {
            this.dialogMessage = $localize`The verification code has expired. Please submit a new code. (${this.electorDataService.userAuthData()?.attemptsRemaining} attempts remaining.)`;
            this.dialogTitle = $localize`Code Expired.`
          }
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'RESEND_TOO_SOON':
          this.dialogTitle = $localize`Resend too soon.`;
          this.dialogMessage = $localize`Please wait at least ${this.electorDataService.resendExpirationTimeMinutes()} minutes before requesting a new verification code.`;
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'MAX_OTP_ATTEMPTS':
          this.electorDataService.userAuthData()!.attemptsRemaining = 0;
          this.maxMFACodeAttemptsReached = true;
          this.dialogMessage = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Maximum Attempts Reached.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case "MAX_OTP_RESENDS":
          this.electorDataService.userAuthData()!.resendsRemaining = 0;
          this.maxMFAResendsReached = true;
          this.dialogMessage = $localize`You have made ${this.electorDataService.userAuthData()?.maxResends ?? 5} resend requests. No more requests are allowed.`;
          this.dialogTitle = $localize`Maximum Attempts Reached.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case "ACCOUNT_LOCKED":
          this.dialogMessage = $localize`Your account has been locked. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Locked Account`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case "ALREADY_LOGGED_IN":
          this.dialogMessage = $localize`You already have an active session. To continue, please close the active session and try again.`;
          this.dialogTitle = $localize`Already Logged In.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;

        case 'MISSING_EMAIL':
          this.dialogMessage = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Missing Email`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;

        case 'MISSING_PHONE':
          this.dialogMessage = $localize`We could not find a phone number on file. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Missing Phone`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;

        case 'INVALID_FORM':
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Malformed Data`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'DECODER_STATE_LOST':
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.openDialog({ type: DialogType.ALERT, title: this.dialogTitle, content: this.dialogMessage});
          break;

        case 'INVALID_MTE_DECODING':
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.openDialog({ type: DialogType.ALERT, title: this.dialogTitle, content: this.dialogMessage});
          break;
        default:
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`
          this.dialogTitle = $localize`Unknown error.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
      }
    }
  }

  resendOTP() {
    this.resetMfaInput();
    let now = Date.now();
    if(now < (this.electorDataService.userAuthData()?.resendExpiration ?? 0) ){
      this.dialogTitle = $localize`Resend too soon.`;
      this.dialogMessage = $localize`Please wait at least ${this.electorDataService.resendExpirationTimeMinutes()} minutes before requesting a new verification code.`;
      this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
    }
    if ((this.electorDataService.userAuthData()?.resendsRemaining ?? 0) <= 0) {
      this.maxMFAResendsReached = true;
      this.dialogMessage = $localize`You have made ${this.electorDataService.userAuthData()?.maxResends ?? 5 } resend requests. No more requests are allowed.`;
      this.dialogTitle = $localize`Maximum Attempts Reached.`;
      this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
    } else {
      this.postMFAMethod();
      this.electorDataService.userAuthData()!.resendsRemaining = (this.electorDataService.userAuthData()?.resendsRemaining ?? 1) - 1;
    }
  }

  async verifyCode() {
    if ((this.electorDataService.userAuthData()?.attemptsRemaining ?? 0) <= 0) {
      this.dialogMessage = $localize`You have exhausted attempts to enter the verification code. Please use the "Contact Support" link for assistance.`;
      this.dialogTitle = $localize`Maximum Attempts Reached.`;
      this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
    }

    let apiRequest = {
      voter_id: this.electorDataService.userAuthData()?.voter_id,
      otp: this.mfaFormGroup.get('code')?.value
    }
    try {
      this.displaySpinner = true;
      let response = await firstValueFrom(this.apiService.postWithHandshakeCheck<{message: string}>(verifyOTPAPI, apiRequest));
      this.displaySpinner = false;
      if(response.body?.message == "SUCCESS") {
        if(this.electorDataService.userAuthData()) {
          this.electorDataService.userAuthData()!.mfaVerified = true;
          this.electorDataService.userAuthData()!.attemptsRemaining = this.electorDataService.userAuthData()?.maxAttempts ?? this.electorDataService.MAX_OTP_ATTEMPTS;
          this.electorDataService.userAuthData()!.resendsRemaining = this.electorDataService.userAuthData()?.maxResends ?? this.electorDataService.MAX_OTP_RESENDS;
        }          
        
        this.dialogTitle = $localize`Success!`;
        this.dialogMessage = $localize`Your multi-factor authentication settings have been changed.`;
        this.openDialog({ type: DialogType.SUCCESS, title: this.dialogTitle, content: this.dialogMessage });

        timer(500).subscribe(() => {
          this.router.navigate(dashboardRoute);
        })
      } else {
        this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`
        this.dialogTitle = $localize`Unknown error.`
        this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
      }
    } catch(err: any) {
      this.resetMfaInput();
      this.displaySpinner = false;
      switch(err.error.error_type) {
        case 'INVALID_OTP':
          this.electorDataService.userAuthData()!.attemptsRemaining = (this.electorDataService.userAuthData()?.attemptsRemaining ?? 1) - 1;
          this.dialogMessage = $localize`The verification code is incorrect. To continue, please try entering it again.`;
          this.dialogTitle = $localize`Invalid Code`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break
        case 'MAX_OTP_ATTEMPTS':
          this.electorDataService.userAuthData()!.attemptsRemaining = 0;
          this.maxMFACodeAttemptsReached = true;
          this.dialogMessage = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Maximum Attempts Reached.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'VERIFY_OTP_TOO_SOON':
          this.dialogTitle = $localize`Verification too soon.`;
          this.dialogMessage = $localize`Important! Please wait at least ${this.electorDataService.mfaExpirationTimeMinutes()} minutes before entering your verification code again.`;
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'UNKNOWN_USER':
          this.dialogTitle = $localize`Information not found`;
          this.dialogMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage })
          break;
        case 'BAD_REQUEST':
          this.dialogMessage = $localize` Please try again or use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Bad Request.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'MISSING_EMAIL':
          this.dialogMessage = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Missing Email`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;

        case 'MISSING_PHONE':
          this.dialogMessage = $localize`We could not find a phone number on file. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Missing Phone`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'INVALID_FORM':
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Malformed Data`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
        case 'DECODER_STATE_LOST':
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.openDialog({ type: DialogType.ALERT, title: this.dialogTitle, content: this.dialogMessage});
          break;

        case 'INVALID_MTE_DECODING':
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.openDialog({ type: DialogType.ALERT, title: this.dialogTitle, content: this.dialogMessage});
          break;

        default:
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`
          this.dialogTitle = $localize`Unknown error.`
          this.openDialog({ type: DialogType.ERROR, title: this.dialogTitle, content: this.dialogMessage });
          break;
      }
    }
  }

  isEmailMfaSetupEnabled(): boolean {
    return this.electorDataService.userAuthData()?.mfaMethod !== 'EMAIL';
  }

  copySecret() {
    if (navigator.clipboard && navigator.clipboard.writeText && this.token) {
      navigator.clipboard.writeText(this.token).then(() => {
        alert($localize`Secret key copied to clipboard.`);
      }).catch(() => {
        this.fallbackCopy();
      });
    } else {
      this.fallbackCopy();
    }
  }

  private fallbackCopy() {
    // Fallback for older browsers
    if(this.token) {
      const textArea = document.createElement('textarea');
      textArea.value = this.token;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert($localize`Secret key copied to clipboard.`);
    }
  }
  
  isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  resetMfaInput() {
    this.mfaInputFields.forEach((input) => {
      input.nativeElement.value = '';
    });
    this.mfaFormGroup.get('code')?.reset();
    this.mfaFormGroup.reset();
  }

  resetTimer() {
    this.allowResends = false;
    this.resendAllowedTimer = timer(this.electorDataService.userAuthData()?.resendExpirationTime ?? 5*60*1000);
    this.resendAllowedTimer.subscribe((done) => {
      this.allowResends = true
    })
  }

  validateNumericInput(value: string): string {
    return /^\d$/.test(value) ? value : '';
  }

  handleInput(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = this.validateNumericInput(inputElement.value.slice(0, 1));
    inputElement.value = value;

    if (!value) {
      this.updateFormControl(); 
      return;
    }

    const nextInput = document.querySelectorAll<HTMLInputElement>('#otp > input')[index + 1];
    if (index === 5) {
      inputElement.select(); 
    } else if (nextInput) {
      nextInput.focus(); 
    }

    this.updateFormControl();
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    const pastedValues = clipboardData.split('').filter((char) => /^\d$/.test(char)); 

    if (pastedValues.length !== 6) {
      this.mfaFormGroup.get('code')?.setErrors({ invalidPaste: true });
      return;
    }

    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    inputs.forEach((input, i) => {
      input.value = pastedValues[i] || ''; 
    });

    inputs[inputs.length - 1]?.focus(); 
    this.updateFormControl();
  }

  handleFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select(); 
  }

  handleBlur(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = this.validateNumericInput(inputElement.value);
    this.updateFormControl();
  }

  updateFormControl(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    const code = Array.from(inputs)
      .map((input) => input.value)
      .join('');

    const control = this.mfaFormGroup.get('code');
    control?.setValue(code);

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      control?.setErrors({ invalidCode: true });
    } else {
      control?.setErrors(null);
    }
  }

  handleKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    
    if (event.key === 'Backspace') {
      if (input.value === '' && index > 0) {
        event.preventDefault();
        this.mfaInputFields.get(index - 1)?.nativeElement.focus();
      }
    }
    
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.mfaInputFields.get(index - 1)?.nativeElement.focus();
    }
    
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.mfaInputFields.get(index + 1)?.nativeElement.focus();
    }
  }

  openDialog(data: any): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      data: data
    });
  } 
}
